import {
  createLink,
  getRouteApi,
  Link,
  useNavigate,
} from '@tanstack/react-router'
import { Skeleton } from 'antd'
import CantileverButton from 'components/CantileverButton'
import { OptionsDropdown } from 'components/OptionsDropdown'
import {
  AssetImage,
  AssetOverviewDataContext,
  AssetOverviewEditorGate,
  AssetOverviewUXContext,
  HAS_MISSING_COMPONENTS_FIELDS,
  HAS_MISSING_DETAILS_FIELDS,
  HAS_MISSING_FEATURES_FIELDS,
  ResolveFieldsBanner,
} from 'features/assetDatabase'
import { BreadcrumbBar } from 'features/breadcrumbBar'
import { FdeCdfSpecificationList } from 'features/dataLayers'
import { MaxWidthEnforcer, usePageMaxWidth } from 'features/maxPageWidth'
import { PermissionsGate, useGeckoPermissions } from 'features/permissions'
import { useRolodexEntries } from 'features/rolodex'
import { BoldableText } from 'pages/assetDatabase/assetOverview/header/BoldableText'
import React, { useContext, useMemo } from 'react'
import styled, { css } from 'styled-components'
import {
  DropdownOption,
  GeckoAssetModelType,
  GeckoView,
  KestrelInspectionType,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_VIEWER,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexEntryType,
} from 'types'

export const AssetViews = {
  INSPECTIONS: 'inspections',
  DETAILS: 'details',
  COMPONENTS: 'components',
  FILES: 'files',
  ACTIVITY: 'activity',
  HISTORY: 'history',
} as const

export type AssetViews = (typeof AssetViews)[keyof typeof AssetViews]

const HeaderAndNavContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  z-index: 10; /* Ensure the header is above other content */
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
`

const HeaderContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: row;
`

const HeaderBorderContainer = styled.div`
  border-bottom: 1px solid
    ${({ theme }) => theme.forecast.cardDividerBackground};
`

const ImageContainer = styled.div<{ $primaryImage: boolean }>`
  position: relative;
  height: 5.75rem;
  aspect-ratio: 4/3;
  overflow: hidden;
  border-radius: 0.25rem;
  background-color: ${({ theme }) => theme.forecast.defaultButton};

  img {
    width: 100%;
    height: 100%;
    object-fit: ${({ $primaryImage }) => ($primaryImage ? 'cover' : 'contain')};
  }
`

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 16px;
`

const TopSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: 44px;
  margin-bottom: 8px;
  cursor: default;
  align-items: center;
`

const TitlesContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledTitle = styled.div`
  display: flex;
  margin-bottom: 4px;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 18px;
  line-height: 20px;
  font-weight: 600;
`

const StyledSubtitles = styled.div`
  display: flex;
  gap: 24px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
`

const BottomSection = styled.div`
  height: 40px;
`

const TabsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px;
  height: 40px;
  gap: 4px;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  border-radius: 6px;
  width: fit-content;
`

const activeButtonStyles = css<{ $isWarning?: boolean }>`
  background-color: ${({ theme, $isWarning }) =>
    $isWarning
      ? `color-mix(in srgb, ${theme.forecast.warning} 80%, transparent 20%)`
      : theme.forecast.defaultButton};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: bold;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }

  &:active {
    background-color: ${({ theme, $isWarning }) =>
      $isWarning
        ? `color-mix(in srgb, ${theme.forecast.warning} 80%, transparent 20%)`
        : theme.forecast.defaultButton};
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const StyledTab = createLink(styled.a<{
  $isWarning?: boolean
}>`
  display: flex;
  align-items: center;
  padding: 4px 8px 4px 8px;
  height: 32px;
  text-decoration: none;
  cursor: pointer;
  border-radius: 0.25rem;
  color: ${({ theme, $isWarning }) =>
    $isWarning ? theme.forecast.defaultText : theme.forecast.secondaryText};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  background-color: ${({ theme, $isWarning }) =>
    $isWarning
      ? `color-mix(in srgb, ${theme.forecast.warning}, transparent 80%)`
      : theme.forecast.defaultGhostButton};

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
  }
  &[data-status='active'] {
    ${activeButtonStyles}
  }
`)

const DropdownContainer = styled.div`
  display: flex;
  gap: 0.25rem;
`

export const AssetHeader: React.FC = () => {
  const { useParams } = getRouteApi('/assets/$siteID/$unitID')
  const { unitID, siteID } = useParams()
  const {
    unit,
    assetDataObj,
    components,
    isLoading,
    hasEverLoaded,
    isPrimaryImageLoaded,
    setIsPrimaryImageLoaded,
  } = useContext(AssetOverviewDataContext)
  const { hasMissingDerivedLayerFields, inResolveMode } = useContext(
    AssetOverviewUXContext
  )
  const navigate = useNavigate()

  const { hasPermissions } = useGeckoPermissions()

  const { kestrelModels } = useRolodexEntries(
    unitID
      ? {
          type: RolodexEntryType.KESTREL_MODEL,
          tags: { unit: unitID },
          filters: [
            {
              key: 'inspection_type',
              op: '=',
              value: KestrelInspectionType.SITEVIEW,
            },
          ],
        }
      : undefined
  )

  const hasSiteviewKestrelModel = kestrelModels.length > 0

  const title = useMemo(() => {
    if (!unit) return ''
    const name = unit.data.name
    const words = name.split('-')
    const capitalizedTitle = words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
    return capitalizedTitle
  }, [unit])

  const isPart = useMemo(
    () =>
      assetDataObj?.assetModel?.data.type ===
      GeckoAssetModelType.MachinedForging,
    [assetDataObj?.assetModel?.data.type]
  )

  const isShip = useMemo(
    () => assetDataObj?.assetModel?.data.type === GeckoAssetModelType.Ship,
    [assetDataObj?.assetModel?.data.type]
  )

  const showResolveFieldsBanner = useMemo(() => {
    //determine if we should show the resolve fields banner
    //if we can create a CDF for Remaining Life, we shouldn't show the banner
    const rlCdfSpecifications = FdeCdfSpecificationList.filter((spec) => {
      return spec.view.id === GeckoView.RemainingLife.id
    })
    return (
      !components?.every((component) => {
        return rlCdfSpecifications.some((spec) => {
          return spec.canCreateCdfData(
            assetDataObj?.assetModel,
            component.id,
            {}
          )
        })
      }) && hasMissingDerivedLayerFields.includes(true)
    )
  }, [assetDataObj?.assetModel, components, hasMissingDerivedLayerFields])

  const hasAssetOverviewEditorPermission = hasPermissions([
    PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR,
    PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN,
  ])

  const { maxWidth } = usePageMaxWidth()

  const viewerOptions: DropdownOption[] = [
    {
      label: 'Configure Full Asset View',
      value: 'configuration',
      disabled: !hasAssetOverviewEditorPermission,
    },
    { label: 'View Full Asset (Beta)', value: 'inspection' },
  ]

  const handleNavigationSelect = (value: string) => {
    if (value === 'configuration') {
      navigate({
        to: '/viewer/$unitID/configuration',
        params: {
          unitID: unitID,
        },
      })
    } else {
      navigate({
        to: '/viewer/$unitID/inspection',
        params: {
          unitID: unitID,
        },
      })
    }
  }

  const isMCVConfigured = useMemo(() => {
    //if any component has a sceneConfig, we consider MCV to be configured
    // Need the Array.from since safari doesn't support array operators on iterators
    return Array.from(
      assetDataObj?.assetModel?.data.components?.values() ?? []
    ).some((d) => {
      return d.sceneConfig
    })
  }, [assetDataObj?.assetModel])

  return (
    <HeaderAndNavContainer>
      {isLoading || !hasEverLoaded ? (
        <Skeleton active paragraph style={{ margin: '20px' }} />
      ) : (
        <>
          <BreadcrumbBar
            lastPage={isPart ? 'Parts' : isShip ? 'Ships' : undefined}
            currentPage={title}
            padding={'0 1rem'}
            navigateTo={
              isPart
                ? '/parts/$siteID'
                : isShip
                  ? '/ships/$siteID'
                  : '/assets/$siteID'
            }
            params={{ siteID: siteID }}
          />
          <HeaderBorderContainer>
            <MaxWidthEnforcer $maxWidth={maxWidth}>
              {showResolveFieldsBanner && (
                <AssetOverviewEditorGate>
                  <ResolveFieldsBanner />
                </AssetOverviewEditorGate>
              )}
              <HeaderContainer>
                <ImageContainer $primaryImage={isPrimaryImageLoaded}>
                  <AssetImage
                    primaryImageID={unit?.data.primary_image}
                    assetModelType={assetDataObj?.assetModel?.data.type}
                    showThumbnail={false}
                    onImageLoadStatus={setIsPrimaryImageLoaded}
                  />
                </ImageContainer>
                <InfoContainer>
                  <TopSection>
                    <TitlesContainer>
                      <StyledTitle>
                        <p>{title}</p>
                      </StyledTitle>
                      <StyledSubtitles>
                        <p>{unit?.data.erp_id || 'ErpID: –'}</p>
                        <p>
                          {unit?.data.area || 'Equip Area: –'},{' '}
                          {unit?.data.area_description || 'Area Description: –'}
                        </p>
                      </StyledSubtitles>
                    </TitlesContainer>

                    <DropdownContainer>
                      {hasSiteviewKestrelModel ? (
                        <Link
                          to={'/systemview/$entryID'}
                          style={{ textDecoration: 'none' }}
                          params={{
                            entryID: unitID,
                          }}>
                          <CantileverButton primary>View Unit</CantileverButton>
                        </Link>
                      ) : (
                        components.length > 0 && (
                          <Link
                            to={'/details/$entryID'}
                            from='/assets/$siteID/$unitID'
                            style={{ textDecoration: 'none' }}
                            params={{
                              entryID: components.at(0)?.id || '',
                            }}>
                            <CantileverButton primary>
                              View Component
                            </CantileverButton>
                          </Link>
                        )
                      )}
                      {isMCVConfigured &&
                        !hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER]) && (
                          <PermissionsGate
                            requires={[
                              PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN,
                              PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR,
                              PERMISSION_CANTILEVER_ASSET_OVERVIEW_VIEWER,
                            ]}>
                            <Link
                              to={'/viewer/$unitID/inspection'}
                              from='/assets/$siteID/$unitID'
                              style={{ textDecoration: 'none' }}>
                              <CantileverButton primary>
                                View Full Asset
                              </CantileverButton>
                            </Link>
                          </PermissionsGate>
                        )}

                      <PermissionsGate
                        requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
                        <OptionsDropdown
                          triggerText=''
                          options={viewerOptions}
                          onSelect={handleNavigationSelect}
                        />
                      </PermissionsGate>
                    </DropdownContainer>
                  </TopSection>
                  <BottomSection>
                    <TabsContainer>
                      {!isPart && (
                        <StyledTab
                          to='.'
                          search={{ view: AssetViews.INSPECTIONS }}
                          replace>
                          {({ isActive }) => (
                            <BoldableText $isActive={isActive}>
                              Inspections
                            </BoldableText>
                          )}
                        </StyledTab>
                      )}
                      <StyledTab
                        $isWarning={
                          hasMissingDerivedLayerFields[
                            HAS_MISSING_DETAILS_FIELDS
                          ] && inResolveMode
                        }
                        to='.'
                        search={{ view: AssetViews.DETAILS }}
                        replace>
                        {({ isActive }) => (
                          <BoldableText $isActive={isActive}>
                            Details
                          </BoldableText>
                        )}
                      </StyledTab>
                      {isPart && (
                        <StyledTab
                          to='.'
                          search={{ view: AssetViews.HISTORY }}
                          replace>
                          {({ isActive }) => (
                            <BoldableText $isActive={isActive}>
                              History
                            </BoldableText>
                          )}
                        </StyledTab>
                      )}
                      <StyledTab
                        to='.'
                        search={{ view: AssetViews.COMPONENTS }}
                        replace
                        $isWarning={
                          (hasMissingDerivedLayerFields[
                            HAS_MISSING_COMPONENTS_FIELDS
                          ] ||
                            hasMissingDerivedLayerFields[
                              HAS_MISSING_FEATURES_FIELDS
                            ]) &&
                          inResolveMode
                        }>
                        {({ isActive }) => (
                          <BoldableText $isActive={isActive}>
                            Components
                          </BoldableText>
                        )}
                      </StyledTab>
                      <StyledTab
                        to='.'
                        search={{ view: AssetViews.FILES }}
                        replace>
                        {({ isActive }) => (
                          <BoldableText $isActive={isActive}>
                            Files
                          </BoldableText>
                        )}
                      </StyledTab>
                      <StyledTab
                        to='.'
                        search={{ view: AssetViews.ACTIVITY }}
                        replace>
                        {({ isActive }) => (
                          <BoldableText $isActive={isActive}>
                            Activity Log
                          </BoldableText>
                        )}
                      </StyledTab>
                    </TabsContainer>
                  </BottomSection>
                </InfoContainer>
              </HeaderContainer>
            </MaxWidthEnforcer>
          </HeaderBorderContainer>
        </>
      )}
    </HeaderAndNavContainer>
  )
}
